import React, { FC, useMemo } from "react";
import NextLink from "next/link";

import SchemaBreadcrumbs from "src/components/index/schemas/SchemaBreadcrumbs";
import SchemaDescription from "src/components/index/schemas/SchemaDescription";
import { AllOrgNamesQuery, Property, Subject } from "src/lib/graphql/types";

import { useUser } from "src/hooks/useUser";
import ForkSubject from "./ForkSubject";
import CreateSchemaChild from "./CreateSchemaChild";
import SearchIcon from "src/components/icons/SearchIcon";
import { useQuery } from "@apollo/client";
import { getAllOrgNames } from "src/lib/graphql/org.queries";

export type SchemaHeaderEntity = Pick<
  Subject | Property,
  "id" | "name" | "namespace" | "description" | "version"
> & {
  ancestors?: Pick<Subject, "name" | "namespace" | "description">[];
};

export type OwnerOptionsEntity = {
  label: string;
  value: string;
}[];

const SchemaHeader: FC<{
  isProperty: boolean;
  schema: SchemaHeaderEntity;
}> = ({ isProperty, schema }) => {
  const [user] = useUser();

  // All properties have the same parent: Property
  const ancestors = isProperty
    ? [
        { name: "Thing", namespace: "schema" },
        { name: "Intangible", namespace: "schema" },
        { name: "Property", namespace: "schema" },
      ]
    : schema.ancestors;

  const complexType = !isProperty && (schema as Subject).type === "ComplexType";

  const canFork = user && !isProperty && complexType;

  const typeLabel = isProperty ? "Property" : complexType ? "Type" : "DataType";

  let orgsData: AllOrgNamesQuery | undefined;
  if (user?.role?.name === "HASHAdmin") {
    ({ data: orgsData } = useQuery<AllOrgNamesQuery>(getAllOrgNames, {
      variables: { namesOnly: true },
    }));
  }

  const ownerOptions: OwnerOptionsEntity = useMemo(() => {
    const options = [
      {
        label: user?.fullName === user?.id ? "yourself" : user?.fullName ?? "",
        value: user?.shortname ?? "",
      },
    ];
    if (user?.role?.name === "HASHAdmin") {
      options.push(
        ...(orgsData?.orgs || []).map((org) => ({
          label: org.name,
          value: org.shortname,
        }))
      );
    } else if (user?.memberOf) {
      options.push(
        ...user.memberOf.map((orgInfo) => ({
          label: orgInfo.org!.name,
          value: orgInfo.org!.shortname,
        }))
      );
    }
    return options;
  }, [orgsData, user]);

  return (
    <div className="project-header">
      <div className="container project-header-content schema-header">
        <SchemaBreadcrumbs
          name={schema.name}
          namespace={schema.namespace}
          ancestors={ancestors}
        />
        <div className="header-flex-container">
          <div className="schema-summary">
            <h3 className="project-title heading-3">
              <strong>{schema.name}</strong> {typeLabel}
            </h3>
            {schema.description ? (
              <p className="schema-description">
                <SchemaDescription description={schema.description} />
              </p>
            ) : null}
          </div>

          <div className="schema-header-buttons">
            {complexType && (
              <div className="search-for-usages icon-button">
                <NextLink href={`/search?subject=${schema.id}`}>
                  <a>
                    <button className="secondary-button bold">
                      Find Instances
                      <div className="white-fill">
                        <SearchIcon height={24} style={{ marginLeft: 7 }} />
                      </div>
                    </button>
                  </a>
                </NextLink>
              </div>
            )}
            {canFork && (
              <>
                <ForkSubject
                  name={schema.name}
                  namespace={schema.namespace}
                  version={schema.version}
                  ownerOptions={ownerOptions}
                />
                <CreateSchemaChild
                  ownerOptions={ownerOptions}
                  schema={schema}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchemaHeader;
